import React, { useState, useEffect, useCallback } from 'react';
import axios from '../../utils/axios';
import toast from 'react-hot-toast';
import {
  ComposedChart,
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const HEBREW_MONTHS = {
  '01': 'ינואר', '02': 'פברואר', '03': 'מרץ',  '04': 'אפריל',
  '05': 'מאי',   '06': 'יוני',   '07': 'יולי',  '08': 'אוגוסט',
  '09': 'ספטמבר','10': 'אוקטובר','11': 'נובמבר','12': 'דצמבר',
};

const TODAY      = new Date().toISOString().split('T')[0];
const YEAR_START = `${new Date().getFullYear()}-01-01`;

const TABS = [
  { id: 'sales',         label: 'מכירות' },
  { id: 'collections',   label: 'גביה' },
  { id: 'items',         label: 'פריטים' },
  { id: 'profitability', label: 'רווחיות' },
  { id: 'debt',          label: 'חובות מול גביה' },
];

const CHART_COLORS = ['#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6','#ec4899'];

/* ─── helpers ──────────────────────────────────────────────────────────────── */

const formatMonth = (ym) => {
  if (!ym) return '';
  const [year, month] = String(ym).split('-');
  return `${HEBREW_MONTHS[month] || month} ${year}`;
};

const formatCurrency = (value) =>
  `₪${parseFloat(value || 0).toLocaleString('he-IL', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const formatPercent = (value) => `${parseFloat(value || 0).toFixed(1)}%`;

const marginBadgeClass = (pct) =>
  pct > 20
    ? 'bg-green-100 text-green-800'
    : pct > 10
    ? 'bg-yellow-100 text-yellow-800'
    : 'bg-red-100 text-red-800';

const rateBadgeClass = (rate) =>
  rate >= 100
    ? 'bg-green-100 text-green-800'
    : rate >= 70
    ? 'bg-yellow-100 text-yellow-800'
    : 'bg-red-100 text-red-800';

const downloadCSV = (rows, columns, filename) => {
  const header = columns.map((c) => c.label).join(',');
  const body = rows.map((row) =>
    columns
      .map((c) => `"${String(row[c.key] ?? '').replace(/"/g, '""')}"`)
      .join(',')
  );
  const csv = [header, ...body].join('\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

/* ─── small reusable components ────────────────────────────────────────────── */

const KpiCard = ({ label, value, sub }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
    <p className="text-sm text-gray-500 mb-1">{label}</p>
    <p className="text-xl font-bold text-gray-900 truncate">{value}</p>
    {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
  </div>
);

const SectionHeader = ({ title, onExport }) => (
  <div className="flex items-center justify-between mb-5">
    <h2 className="text-lg font-semibold text-gray-800">{title}</h2>
    {onExport && (
      <button
        onClick={onExport}
        className="flex items-center gap-1.5 text-sm text-blue-600 border border-blue-200 px-3 py-1.5 rounded-lg hover:bg-blue-50 transition-colors"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
        ייצוא CSV
      </button>
    )}
  </div>
);

const LoadingState = () => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="h-24 bg-gray-200 animate-pulse rounded-xl" />
      ))}
    </div>
    <div className="h-72 bg-gray-200 animate-pulse rounded-xl" />
    <div className="h-52 bg-gray-200 animate-pulse rounded-xl" />
  </div>
);

const EmptyState = () => (
  <div className="flex flex-col items-center justify-center py-24 text-gray-400 bg-white rounded-xl border border-gray-200">
    <svg className="w-16 h-16 mb-4 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1}
        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
    <p className="text-lg font-medium">אין נתונים להצגה</p>
    <p className="text-sm mt-1">נסה לשנות את טווח התאריכים</p>
  </div>
);

const ChartCard = ({ title, children }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
    {title && <h3 className="text-sm font-medium text-gray-600 mb-4">{title}</h3>}
    <div className="h-72">
      <ResponsiveContainer width="100%" height="100%">
        {children}
      </ResponsiveContainer>
    </div>
  </div>
);

/* ─── main component ────────────────────────────────────────────────────────── */

const ProfitabilityAnalysis = () => {
  const [activeTab,    setActiveTab]    = useState('sales');
  const [dateFrom,     setDateFrom]     = useState(YEAR_START);
  const [dateTo,       setDateTo]       = useState(TODAY);
  const [appliedFrom,  setAppliedFrom]  = useState(YEAR_START);
  const [appliedTo,    setAppliedTo]    = useState(TODAY);
  const [data,         setData]         = useState(null);
  const [loading,      setLoading]      = useState(false);
  const [itemsGroupBy, setItemsGroupBy] = useState('category');
  const [itemsPage,    setItemsPage]    = useState(1);

  // Reset pagination when the grouping or date range changes
  useEffect(() => {
    setItemsPage(1);
  }, [itemsGroupBy, appliedFrom, appliedTo]);

  const fetchData = useCallback(async (tab, from, to, groupBy) => {
    setLoading(true);
    setData(null);
    try {
      const params = { date_from: from, date_to: to };
      let res;
      switch (tab) {
        case 'sales':
          res = await axios.get('/api/reports/monthly-sales', { params });
          break;
        case 'collections':
          res = await axios.get('/api/reports/monthly-collections', { params });
          break;
        case 'items':
          res = await axios.get('/api/reports/monthly-items-sold', {
            params: { ...params, group_by: groupBy },
          });
          break;
        case 'profitability':
          res = await axios.get('/api/admin/dashboard/profitability-analysis', { params });
          break;
        case 'debt':
          res = await axios.get('/api/reports/debt-vs-collections', { params });
          break;
        default:
          return;
      }
      setData(res.data);
    } catch {
      toast.error('שגיאה בטעינת הנתונים');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(activeTab, appliedFrom, appliedTo, itemsGroupBy);
  }, [activeTab, appliedFrom, appliedTo, itemsGroupBy, fetchData]);

  const handleApply = () => {
    if (dateFrom === appliedFrom && dateTo === appliedTo) {
      fetchData(activeTab, appliedFrom, appliedTo, itemsGroupBy);
    } else {
      setAppliedFrom(dateFrom);
      setAppliedTo(dateTo);
    }
  };

  const setQuickPreset = (preset) => {
    const now = new Date();
    let from = YEAR_START;
    if (preset === 'month') {
      from = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
    } else if (preset === '3months') {
      const d = new Date(now);
      d.setMonth(d.getMonth() - 3);
      from = d.toISOString().split('T')[0];
    }
    setDateFrom(from);
    setDateTo(TODAY);
    setAppliedFrom(from);
    setAppliedTo(TODAY);
  };

  /* ── Tab 1: מכירות לפי חודש ─────────────────────────────────────────────── */

  const renderSalesTab = () => {
    const months = data?.months || (Array.isArray(data) ? data : []);
    if (!months.length) return <EmptyState />;

    const summary       = data?.summary || {};
    const totalRevenue  = summary.total_revenue  || months.reduce((s, m) => s + (m.total_revenue  || 0), 0);
    const totalOrders   = summary.total_orders   || months.reduce((s, m) => s + (m.total_orders   || 0), 0);
    const b2bRevenue    = summary.b2b_revenue    || months.reduce((s, m) => s + (m.b2b_revenue    || 0), 0);
    const b2cRevenue    = summary.b2c_revenue    || months.reduce((s, m) => s + (m.b2c_revenue    || 0), 0);
    const avgOrder      = totalOrders > 0 ? totalRevenue / totalOrders : 0;

    const chartData = months.map((m) => ({
      month:    formatMonth(m.month),
      'הכנסות': m.total_revenue || 0,
      'הזמנות': m.total_orders  || 0,
    }));

    const exportColumns = [
      { key: 'month',       label: 'חודש' },
      { key: 'orders',      label: 'הזמנות' },
      { key: 'revenue',     label: 'הכנסות (₪)' },
      { key: 'b2b',         label: 'B2B (₪)' },
      { key: 'b2c',         label: 'B2C (₪)' },
      { key: 'avg',         label: 'ממוצע להזמנה (₪)' },
      { key: 'unpaid',      label: 'לא שולם (₪)' },
    ];
    const exportData = months.map((m) => ({
      month:   formatMonth(m.month),
      orders:  m.total_orders    || 0,
      revenue: m.total_revenue   || 0,
      b2b:     m.b2b_revenue     || 0,
      b2c:     m.b2c_revenue     || 0,
      avg:     m.avg_order_value || 0,
      unpaid:  m.unpaid_amount   || 0,
    }));

    return (
      <div className="space-y-6">
        <SectionHeader
          title="מכירות לפי חודש"
          onExport={() => downloadCSV(exportData, exportColumns, 'מכירות.csv')}
        />

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard label='סה"כ הכנסות'   value={formatCurrency(totalRevenue)} />
          <KpiCard label='סה"כ הזמנות'   value={totalOrders.toLocaleString('he-IL')} />
          <KpiCard label="ממוצע להזמנה"  value={formatCurrency(avgOrder)} />
          <KpiCard
            label="B2B / B2C"
            value={`${formatCurrency(b2bRevenue)} / ${formatCurrency(b2cRevenue)}`}
          />
        </div>

        <ChartCard title="הכנסות והזמנות לפי חודש">
          <ComposedChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="month" tick={{ fontSize: 12 }} />
            <YAxis
              yAxisId="rev"
              orientation="right"
              tickFormatter={(v) => `₪${(v / 1000).toFixed(0)}k`}
              tick={{ fontSize: 11 }}
            />
            <YAxis yAxisId="ord" orientation="left" tick={{ fontSize: 11 }} />
            <Tooltip
              formatter={(v, name) =>
                name === 'הכנסות' ? [formatCurrency(v), name] : [v.toLocaleString('he-IL'), name]
              }
            />
            <Legend />
            <Bar yAxisId="rev" dataKey="הכנסות" fill="#3b82f6" radius={[4, 4, 0, 0]} />
            <Area
              yAxisId="ord"
              type="monotone"
              dataKey="הזמנות"
              stroke="#f59e0b"
              fill="#fef3c7"
              strokeWidth={2}
            />
          </ComposedChart>
        </ChartCard>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {['חודש', 'הזמנות', 'הכנסות', 'B2B', 'B2C', 'ממוצע להזמנה', 'לא שולם'].map((h) => (
                    <th key={h} className="px-4 py-3 text-right text-xs font-medium text-gray-500 whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {months.map((m, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">
                      {formatMonth(m.month)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {(m.total_orders || 0).toLocaleString('he-IL')}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{formatCurrency(m.total_revenue)}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{formatCurrency(m.b2b_revenue)}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{formatCurrency(m.b2c_revenue)}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{formatCurrency(m.avg_order_value)}</td>
                    <td className="px-4 py-3 text-sm text-red-500">{formatCurrency(m.unpaid_amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  /* ── Tab 2: גביה לפי חודש ───────────────────────────────────────────────── */

  const renderCollectionsTab = () => {
    const months = data?.months || (Array.isArray(data) ? data : []);
    if (!months.length) return <EmptyState />;

    const summary        = data?.summary || {};
    const totalCollected = summary.total_collected  || months.reduce((s, m) => s + (m.total_collected || 0), 0);
    const totalCash      = summary.cash             || months.reduce((s, m) => s + (m.cash           || 0), 0);
    const totalChecks    = summary.checks           || months.reduce((s, m) => s + (m.checks         || 0), 0);
    const totalBank      = summary.bank_transfers   || months.reduce((s, m) => s + (m.bank_transfers || 0), 0);

    const chartData = months.map((m) => ({
      month:     formatMonth(m.month),
      'מזומן':   m.cash           || 0,
      "צ'קים":   m.checks         || 0,
      'העברות':  m.bank_transfers || 0,
    }));

    const exportColumns = [
      { key: 'month',  label: 'חודש' },
      { key: 'total',  label: 'סה"כ גבייה (₪)' },
      { key: 'cash',   label: 'מזומן (₪)' },
      { key: 'checks', label: "צ'קים (₪)" },
      { key: 'bank',   label: 'העברות בנקאיות (₪)' },
      { key: 'count',  label: 'מספר תשלומים' },
    ];
    const exportData = months.map((m) => ({
      month:   formatMonth(m.month),
      total:   m.total_collected  || 0,
      cash:    m.cash             || 0,
      checks:  m.checks           || 0,
      bank:    m.bank_transfers   || 0,
      count:   m.payment_count    || 0,
    }));

    return (
      <div className="space-y-6">
        <SectionHeader
          title="גביה לפי חודש"
          onExport={() => downloadCSV(exportData, exportColumns, 'גביה.csv')}
        />

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard label='סה"כ גבייה'         value={formatCurrency(totalCollected)} />
          <KpiCard label="מזומן"               value={formatCurrency(totalCash)} />
          <KpiCard label="צ'קים"               value={formatCurrency(totalChecks)} />
          <KpiCard label="העברות בנקאיות"      value={formatCurrency(totalBank)} />
        </div>

        <ChartCard title="גביה לפי שיטת תשלום">
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="month" tick={{ fontSize: 12 }} />
            <YAxis tickFormatter={(v) => `₪${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
            <Tooltip formatter={(v, name) => [formatCurrency(v), name]} />
            <Legend />
            <Bar dataKey="מזומן"  stackId="a" fill="#10b981" />
            <Bar dataKey="צ'קים" stackId="a" fill="#3b82f6" />
            <Bar dataKey="העברות" stackId="a" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ChartCard>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {['חודש', 'סה"כ', 'מזומן', "צ'קים", 'העברות בנקאיות', 'מס׳ תשלומים'].map((h) => (
                    <th key={h} className="px-4 py-3 text-right text-xs font-medium text-gray-500 whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {months.map((m, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">
                      {formatMonth(m.month)}
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                      {formatCurrency(m.total_collected)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{formatCurrency(m.cash)}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{formatCurrency(m.checks)}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{formatCurrency(m.bank_transfers)}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {(m.payment_count || 0).toLocaleString('he-IL')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  /* ── Tab 3: פריטים שנמכרו ───────────────────────────────────────────────── */

  const renderItemsTab = () => {
    const topItems    = data?.top_items   || data?.items   || [];
    const monthlyData = data?.monthly_data || [];
    const topGroups   = (data?.top_categories || data?.top_groups || []).slice(0, 6);
    const isProduct   = itemsGroupBy === 'product';

    if (!topItems.length && !monthlyData.length) return <EmptyState />;

    const chartData = monthlyData.map((m) => {
      const row = { month: formatMonth(m.month) };
      if (m.groups) {
        topGroups.forEach((g) => { row[g] = m.groups[g] || 0; });
      }
      return row;
    });

    const exportColumns = isProduct
      ? [
          { key: 'rank',       label: 'דירוג' },
          { key: 'name',       label: 'מוצר' },
          { key: 'sku',        label: 'SKU' },
          { key: 'category',   label: 'קטגוריה' },
          { key: 'units_sold', label: 'יחידות שנמכרו' },
          { key: 'revenue',    label: 'הכנסות (₪)' },
        ]
      : [
          { key: 'rank',       label: 'דירוג' },
          { key: 'name',       label: 'קטגוריה' },
          { key: 'units_sold', label: 'יחידות שנמכרו' },
          { key: 'revenue',    label: 'הכנסות (₪)' },
        ];

    // Pagination (products view only - categories list is short)
    const PAGE_SIZE  = 20;
    const totalPages = Math.max(1, Math.ceil(topItems.length / PAGE_SIZE));
    const page       = Math.min(itemsPage, totalPages);
    const pageStart  = isProduct ? (page - 1) * PAGE_SIZE : 0;
    const pagedItems = isProduct ? topItems.slice(pageStart, pageStart + PAGE_SIZE) : topItems;

    const tableRows = pagedItems.map((item, i) => ({
      ...item,
      rank: pageStart + i + 1,
    }));

    // CSV always exports the FULL list, not just the current page
    const exportRows = topItems.map((item, i) => ({ ...item, rank: i + 1 }));

    const rankStyle = (i) =>
      i === 0
        ? 'bg-yellow-100 text-yellow-700'
        : i === 1
        ? 'bg-gray-200 text-gray-700'
        : i === 2
        ? 'bg-orange-100 text-orange-700'
        : 'bg-gray-100 text-gray-500';

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-800">פריטים שנמכרו</h2>
          <div className="flex items-center gap-3">
            <div className="flex bg-gray-100 rounded-lg p-1 gap-1">
              <button
                onClick={() => setItemsGroupBy('category')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  itemsGroupBy === 'category'
                    ? 'bg-white text-blue-700 shadow-sm'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                לפי קטגוריה
              </button>
              <button
                onClick={() => setItemsGroupBy('product')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  itemsGroupBy === 'product'
                    ? 'bg-white text-blue-700 shadow-sm'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                לפי מוצר
              </button>
            </div>
            <button
              onClick={() => downloadCSV(exportRows, exportColumns, `פריטים-${itemsGroupBy}.csv`)}
              className="flex items-center gap-1.5 text-sm text-blue-600 border border-blue-200 px-3 py-1.5 rounded-lg hover:bg-blue-50 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              ייצוא CSV
            </button>
          </div>
        </div>

        {chartData.length > 0 && topGroups.length > 0 && (
          <ChartCard title={isProduct ? 'מוצרים מובילים לפי חודש' : 'קטגוריות מובילות לפי חודש'}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              {topGroups.map((g, i) => (
                <Bar key={g} dataKey={g} fill={CHART_COLORS[i % CHART_COLORS.length]} radius={[2, 2, 0, 0]} />
              ))}
            </BarChart>
          </ChartCard>
        )}

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            <h3 className="text-sm font-medium text-gray-700">
              {isProduct ? `מוצרים שנמכרו (${topItems.length.toLocaleString('he-IL')})` : 'קטגוריות מובילות'}
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">#</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">
                    {isProduct ? 'מוצר' : 'קטגוריה'}
                  </th>
                  {isProduct && (
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">SKU</th>
                  )}
                  {isProduct && (
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">קטגוריה</th>
                  )}
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">יחידות שנמכרו</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">הכנסות</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {tableRows.map((item, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${rankStyle(item.rank - 1)}`}
                      >
                        {item.rank}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.name}</td>
                    {isProduct && (
                      <td className="px-4 py-3 text-sm text-gray-400">{item.sku || '—'}</td>
                    )}
                    {isProduct && (
                      <td className="px-4 py-3 text-sm text-gray-600">{item.category || '—'}</td>
                    )}
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {(item.units_sold || 0).toLocaleString('he-IL')}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{formatCurrency(item.revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {isProduct && totalPages > 1 && (
            <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 bg-gray-50">
              <button
                onClick={() => setItemsPage(page - 1)}
                disabled={page <= 1}
                className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                הקודם
              </button>
              <span className="text-sm text-gray-600">
                עמוד {page.toLocaleString('he-IL')} מתוך {totalPages.toLocaleString('he-IL')}
                {' · '}
                מציג {(pageStart + 1).toLocaleString('he-IL')}–{Math.min(pageStart + PAGE_SIZE, topItems.length).toLocaleString('he-IL')} מתוך {topItems.length.toLocaleString('he-IL')} מוצרים
              </span>
              <button
                onClick={() => setItemsPage(page + 1)}
                disabled={page >= totalPages}
                className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                הבא
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  /* ── Tab 4: רווחיות ─────────────────────────────────────────────────────── */

  const renderProfitabilityTab = () => {
    const summary    = data?.summary  || data?.overall || {};
    const dailyData  = data?.daily_revenue || data?.daily || data?.trends?.daily || [];
    const products   = data?.products   || [];
    const categories = data?.categories || [];

    if (!Object.keys(summary).length && !dailyData.length && !products.length) return <EmptyState />;

    const totalRevenue = summary.total_revenue || summary.totalRevenue || 0;
    const grossProfit  = summary.gross_profit  || summary.grossProfit  || 0;
    const avgMargin    = summary.avg_margin    || summary.avgMargin    || 0;
    const totalOrders  = summary.total_orders  || summary.totalOrders  || 0;

    const chartData = dailyData.map((d) => ({
      date:      new Date(d.date).toLocaleDateString('he-IL', { month: 'short', day: 'numeric' }),
      'הכנסות':  d.revenue      || d.dailyRevenue || 0,
      'רווח':    d.profit       || d.dailyProfit  || 0,
    }));

    const productsExportColumns = [
      { key: 'name',       label: 'מוצר' },
      { key: 'units_sold', label: 'יחידות שנמכרו' },
      { key: 'revenue',    label: 'הכנסות (₪)' },
      { key: 'profit',     label: 'רווח (₪)' },
      { key: 'margin',     label: 'מרווח (%)' },
    ];

    return (
      <div className="space-y-6">
        <SectionHeader
          title="ניתוח רווחיות"
          onExport={
            products.length
              ? () => downloadCSV(products, productsExportColumns, 'רווחיות-מוצרים.csv')
              : undefined
          }
        />

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard label='סה"כ הכנסות' value={formatCurrency(totalRevenue)} />
          <KpiCard label="רווח גולמי"  value={formatCurrency(grossProfit)} />
          <KpiCard label="מרווח ממוצע" value={formatPercent(avgMargin)} />
          <KpiCard label="הזמנות"      value={totalOrders.toLocaleString('he-IL')} />
        </div>

        {chartData.length > 0 && (
          <ChartCard title="הכנסות ורווח יומי">
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="gradRev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#3b82f6" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gradProfit" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#10b981" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={(v) => `₪${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v, name) => [formatCurrency(v), name]} />
              <Legend />
              <Area
                type="monotone" dataKey="הכנסות"
                stroke="#3b82f6" fill="url(#gradRev)" strokeWidth={2}
              />
              <Area
                type="monotone" dataKey="רווח"
                stroke="#10b981" fill="url(#gradProfit)" strokeWidth={2}
              />
            </AreaChart>
          </ChartCard>
        )}

        {products.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100">
              <h3 className="text-sm font-medium text-gray-700">רווחיות לפי מוצרים</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {['מוצר', 'יחידות שנמכרו', 'הכנסות', 'רווח', 'מרווח'].map((h) => (
                      <th key={h} className="px-4 py-3 text-right text-xs font-medium text-gray-500">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {products.map((p, i) => {
                    const margin = p.margin || p.marginPercentage || p.margin_percentage || 0;
                    return (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{p.name}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {(p.units_sold || p.totalSold || 0).toLocaleString('he-IL')}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">{formatCurrency(p.revenue)}</td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          {formatCurrency(p.profit)}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${marginBadgeClass(margin)}`}>
                            {formatPercent(margin)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {categories.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100">
              <h3 className="text-sm font-medium text-gray-700">רווחיות לפי קטגוריות</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {['קטגוריה', 'יחידות שנמכרו', 'הכנסות', 'רווח', 'מרווח ממוצע'].map((h) => (
                      <th key={h} className="px-4 py-3 text-right text-xs font-medium text-gray-500">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {categories.map((c, i) => {
                    const avgMarginCat = c.avg_margin || c.avgMargin || 0;
                    return (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{c.name}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {(c.units_sold || c.totalSold || 0).toLocaleString('he-IL')}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">{formatCurrency(c.revenue)}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{formatCurrency(c.profit)}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${marginBadgeClass(avgMarginCat)}`}>
                            {formatPercent(avgMarginCat)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  /* ── Tab 5: חובות מול גביה ──────────────────────────────────────────────── */

  const renderDebtTab = () => {
    const months = data?.months || (Array.isArray(data) ? data : []);
    if (!months.length) return <EmptyState />;

    const summary        = data?.summary || {};
    const totalSales     = summary.total_sales      || months.reduce((s, m) => s + (m.new_orders_amount || 0), 0);
    const totalCollected = summary.total_collected  || months.reduce((s, m) => s + (m.collected_amount  || 0), 0);
    const gap            = totalSales - totalCollected;
    const collectionRate = totalSales > 0 ? (totalCollected / totalSales) * 100 : 0;

    const chartData = months.map((m) => ({
      month:           formatMonth(m.month),
      'הזמנות חדשות': m.new_orders_amount || 0,
      'גבייה':         m.collected_amount  || 0,
      'אחוז גבייה':    m.collection_rate   || 0,
    }));

    const exportColumns = [
      { key: 'month',      label: 'חודש' },
      { key: 'new_orders', label: 'הזמנות חדשות (₪)' },
      { key: 'collected',  label: 'גבייה (₪)' },
      { key: 'delta',      label: 'פער (₪)' },
      { key: 'rate',       label: 'אחוז גבייה (%)' },
    ];
    const exportData = months.map((m) => ({
      month:      formatMonth(m.month),
      new_orders: m.new_orders_amount || 0,
      collected:  m.collected_amount  || 0,
      delta:      (m.new_orders_amount || 0) - (m.collected_amount || 0),
      rate:       m.collection_rate   || 0,
    }));

    return (
      <div className="space-y-6">
        <SectionHeader
          title="חובות מול גביה"
          onExport={() => downloadCSV(exportData, exportColumns, 'חובות-גביה.csv')}
        />

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard label='סה"כ מכירות B2B' value={formatCurrency(totalSales)} />
          <KpiCard label='סה"כ גבייה'       value={formatCurrency(totalCollected)} />
          <KpiCard
            label="פער"
            value={formatCurrency(gap)}
            sub={gap > 0 ? 'יתרת חוב' : 'גביה עודפת'}
          />
          <KpiCard label="אחוז גבייה" value={formatPercent(collectionRate)} />
        </div>

        <ChartCard title="הזמנות מול גבייה לפי חודש">
          <ComposedChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="month" tick={{ fontSize: 12 }} />
            <YAxis
              yAxisId="amount"
              orientation="right"
              tickFormatter={(v) => `₪${(v / 1000).toFixed(0)}k`}
              tick={{ fontSize: 11 }}
            />
            <YAxis
              yAxisId="rate"
              orientation="left"
              domain={[0, 100]}
              tickFormatter={(v) => `${v}%`}
              tick={{ fontSize: 11 }}
            />
            <Tooltip
              formatter={(v, name) =>
                name === 'אחוז גבייה' ? [`${(v || 0).toFixed(1)}%`, name] : [formatCurrency(v), name]
              }
            />
            <Legend />
            <Bar yAxisId="amount" dataKey="הזמנות חדשות" fill="#ef4444" radius={[4, 4, 0, 0]} opacity={0.85} />
            <Bar yAxisId="amount" dataKey="גבייה"         fill="#10b981" radius={[4, 4, 0, 0]} opacity={0.85} />
            <Area
              yAxisId="rate"
              type="monotone"
              dataKey="אחוז גבייה"
              stroke="#f59e0b"
              fill="#fef3c7"
              strokeWidth={2}
            />
          </ComposedChart>
        </ChartCard>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {['חודש', 'הזמנות חדשות', 'גבייה', 'פער', 'אחוז גבייה'].map((h) => (
                    <th key={h} className="px-4 py-3 text-right text-xs font-medium text-gray-500 whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {months.map((m, i) => {
                  const delta = (m.new_orders_amount || 0) - (m.collected_amount || 0);
                  const rate  = m.collection_rate || 0;
                  return (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">
                        {formatMonth(m.month)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {formatCurrency(m.new_orders_amount)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {formatCurrency(m.collected_amount)}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium">
                        <span className={rate >= 100 ? 'text-green-600' : rate >= 70 ? 'text-yellow-600' : 'text-red-600'}>
                          {formatCurrency(delta)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${rateBadgeClass(rate)}`}>
                          {formatPercent(rate)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  /* ── render ─────────────────────────────────────────────────────────────── */

  const renderTabContent = () => {
    if (loading) return <LoadingState />;
    if (!data)   return <EmptyState />;
    switch (activeTab) {
      case 'sales':         return renderSalesTab();
      case 'collections':   return renderCollectionsTab();
      case 'items':         return renderItemsTab();
      case 'profitability': return renderProfitabilityTab();
      case 'debt':          return renderDebtTab();
      default:              return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" dir="rtl">
      <div className="max-w-7xl mx-auto p-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">דוחות עסקיים</h1>
          <p className="text-gray-500 mt-1 text-sm">ניתוח מכירות, גביה ורווחיות</p>
        </div>

        {/* Filter Bar */}
        <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6 flex flex-wrap items-center gap-3 shadow-sm">
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600 whitespace-nowrap">מתאריך</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600 whitespace-nowrap">עד</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button
            onClick={handleApply}
            className="bg-blue-600 text-white px-4 py-1.5 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
          >
            הצג
          </button>
          <div className="flex items-center gap-1 border-r border-gray-200 pr-3 mr-1">
            {[
              { key: 'month',   label: 'החודש' },
              { key: '3months', label: '3 חודשים' },
              { key: 'year',    label: 'השנה' },
            ].map((p) => (
              <button
                key={p.key}
                onClick={() => setQuickPreset(p.key)}
                className="text-sm text-blue-600 hover:bg-blue-50 px-2.5 py-1 rounded-lg transition-colors"
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Buttons */}
        <div className="flex gap-1 mb-6 bg-white border border-gray-200 rounded-xl p-1 shadow-sm">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-2.5 px-3 rounded-lg text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {renderTabContent()}
      </div>
    </div>
  );
};

export default ProfitabilityAnalysis;
